<template>
  <div class="gmb-qa-edit" @scroll.passive="onScroll($event.srcElement, $refs.fixedElement.$el)">
    <screen-header
      class="gmb-qa-edit__header"
      :title="$t('gmbQa.title')"
      :breadcrumbs="breadcrumbs"
      :is-loading="isLoading && !isScrolling"
      :right-content="true"
      ref="fixedElement"
    >
      <skeleton-icon v-if="isLoading" margin-left="16px" width="44px" height="44px" />
    </screen-header>
    <screen-container :full-height="true">
      <screen-grid :full-height="true">
        <screen-card :full-height="true" :noPadding="true">
          <template v-slot:body>
            <div class="gmb-qa-edit__content">
              <questions-panel
                v-if="!isMobileOrTablet || (isMobileOrTablet && currentQuestionsAnswersDetail == null)"
                :isLoading="isLoading || isLoadingLocationAccount"
                :isScrolling="isScrolling"
                :locationName="currentLocation.locationName"
                :currentQuestions="currentQuestionsAnswers"
                :pagination="currentQuestionsAnswersPagination"
                :accountId="currentAccountName"
                :locationId="currentLocationId"
                :ROLE_MERCHANT="ROLE_MERCHANT"
                @selectQuestionInDataList="selectQuestionInDataList"
                @onClickQuestionsPaginationButton="onClickQuestionsPaginationButton"
                @search="loadGmbQa"
              />
              <responses-panel
                v-if="!isMobileOrTablet || (isMobileOrTablet && currentQuestionsAnswersDetail != null)"
                :isLoading="isLoadingDetail"
                :isScrolling="isScrolling"
                :hasMoreAnswersToLoad="false"
                :selectedQuestion="currentQuestionsAnswersDetail"
                :respondAccount="{
                  clientLogo: currentClient.logo,
                  locationName: currentLocation.locationName,
                  role: ROLE_MERCHANT,
                }"
                :current-account-name="currentAccountName"
                :ROLE_MERCHANT="ROLE_MERCHANT"
                @dataUpdate="reloadData"
              />
            </div>
          </template>
        </screen-card>
      </screen-grid>
    </screen-container>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import ScreenHeader from '@/components/Screen/Header.vue'
import ScreenContainer from '@/components/Screen/Container.vue'
import ScreenGrid from '@/components/Screen/Grid.vue'
import ScreenCard from '@/components/Screen/Card.vue'
import SkeletonIcon from '@/components/Skeleton/Icon.vue'
import QuestionsPanel from '@/components/Qa/QuestionsPanel.vue'
import ResponsesPanel from '@/components/Qa/ResponsesPanel.vue'
import { onScroll } from '@/mixins/scroll.mixin'

export default {
  name: 'GmbQaEdit',
  components: {
    ScreenHeader,
    ScreenContainer,
    ScreenGrid,
    ScreenCard,
    SkeletonIcon,
    QuestionsPanel,
    ResponsesPanel,
  },
  mixins: [onScroll],
  data() {
    return {
      isLoading: false,
      isLoadingDetail: false,
      isLoadingLocationAccount: false,
      isScrolling: false,
      ROLE_MERCHANT: 'MERCHANT',
    }
  },
  async created() {
    this.isLoadingLocationAccount = true
    await Promise.all([
      this.loadLocation({
        locationName: this.currentLocationId,
        currentAccountName: this.currentAccountName,
      }),
      this.getAccounts({}),
    ])
    this.isLoadingLocationAccount = false

    if (!this.$route.params.name) {
      this.$router.replace({
        ...this.$route,
        params: { ...this.$route.params, name: this.accounts[0].name },
      })
    }
  },
  watch: {
    '$route.params.name': {
      immediate: true,
      async handler() {
        if (this.$route.params.name) {
          await this.loadGmbQa()
        }
      },
    },
  },
  computed: {
    ...mapState({
      accounts: state => state.gmb.accounts,
      currentLocation: state => state.gmb.currentLocation,
      currentQuestionsAnswers: state => state.qa.currentQuestionsAnswers?.data || [],
      currentQuestionsAnswersPagination: state =>
        state.qa.currentQuestionsAnswers?.pagination || { page: 1, nbPages: 1 },
      currentQuestionsAnswersDetail: state => state.qa.currentQuestionsAnswersDetail,
      currentClient: state => state.client.currentClient,
    }),
    isMobileOrTablet() {
      return this.isMobile && this.isTablet
    },
    currentLocationId() {
      return this.$route.params.name.substring(
        this.$route.params.name.indexOf('locations/'),
        this.$route.params.name.length
      )
    },
    currentAccountName() {
      return this.$route.params.name.split('/locations')[0]
    },
    currentAccount() {
      return this.accounts.find(account => account.name === this.currentAccountName)
    },
    breadcrumbs() {
      return [
        {
          label: this.$t('gmbQa.breadcrumb'),
          route: {
            name: 'GmbQa',
            params: {
              name: this.currentAccountName,
            },
          },
        },
        {
          label: this.$t('gmbQaEdit.breadcrumb'),
          route: {
            name: 'GmbQaEdit',
            params: {
              name: this.currentAccountName,
            },
          },
        },
      ]
    },
    hasMoreQuestionToLoad() {
      const { nbPages, page } = this.currentQuestionsAnswersPagination
      return nbPages > page
    },
  },
  methods: {
    ...mapActions({
      loadQuestionsAnswers: 'qa/loadQuestionsAnswers',
      getQuestionsAnswersDetail: 'qa/getQuestionsAnswersDetail',
      resetQuestion: 'qa/resetQuestion',
      getAccounts: 'gmb/getAccounts',
      loadLocation: 'gmb/loadLocation',
    }),
    async reloadData() {
      this.isLoadingDetail = true
      await this.loadGmbQa()
      const selectQuestion = this.currentQuestionsAnswers.find(
        qa => qa.name === this.currentQuestionsAnswersDetail.name
      )
      if (selectQuestion) {
        this.selectQuestionInDataList(selectQuestion)
      } else {
        this.resetQuestion()
        this.isLoadingDetail = false
      }
    },
    async loadGmbQa(search = '') {
      this.isLoading = true
      try {
        await this.loadQuestionsAnswers({
          account: this.currentAccountName,
          location: this.currentLocationId,
          search,
        })
      } finally {
        this.isLoading = false
      }
    },
    async loadMoreGmbQa(page) {
      this.isLoading = true
      try {
        await this.loadQuestionsAnswers({
          account: this.currentAccountName,
          location: this.currentLocationId,
          page: page || this.currentQuestionsAnswersPagination.page + 1,
        })
      } finally {
        this.isLoading = false
      }
    },
    async selectQuestionInDataList(data) {
      this.isLoadingDetail = true
      try {
        await this.getQuestionsAnswersDetail({
          account: this.currentAccountName,
          location: data.location,
          id: data.name,
        })
      } finally {
        this.isLoadingDetail = false
      }
    },
    async onClickQuestionsPaginationButton(page = null) {
      await this.loadMoreGmbQa(page)
    },
  },
}
</script>

<style lang="scss" scoped>
.gmb-qa-edit {
  ::v-deep.ui-data-list__item {
    padding: 0;
  }

  &__header {
    &__cta {
      margin-left: $gutter-mobile;

      @media (min-width: $screen-sm) {
        margin-left: $gutter-tablet;
      }

      @media (min-width: $screen-xl) {
        margin-left: $gutter-desktop;
      }
    }
  }
  &__content {
    display: flex;
    justify-content: flex-start;
    height: 100%;
  }
}
</style>
